import React, { forwardRef, memo, useCallback, useImperativeHandle, useRef, useState } from 'react';

interface VirtualScrollListProps<T> {
    items: T[];
    itemHeight: number;
    height: number;
    overscan?: number;
    renderItem: (item: T, index: number) => React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
    onScroll?: (scrollTop: number) => void;
}

export interface VirtualScrollListRef {
    scrollToIndex: (index: number) => void;
    getContainer: () => HTMLDivElement | null;
}

// ─── Memoized row ─────────────────────────────────────────────────────────────

interface VirtualRowProps {
    height: number;
    children: React.ReactNode;
}

const VirtualRow = memo(({ height, children }: VirtualRowProps) => (
    <div data-scroll-item style={{ height, boxSizing: 'border-box' as const }}>
        {children}
    </div>
));

VirtualRow.displayName = 'VirtualRow';

// ─── Component ────────────────────────────────────────────────────────────────

function VirtualScrollListInner<T>(
    { items, itemHeight, height, overscan = 4, renderItem, className, style, onScroll }: VirtualScrollListProps<T>,
    ref: React.ForwardedRef<VirtualScrollListRef>
) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [scrollTop, setScrollTop] = useState(0);
    const rafRef = useRef<number | null>(null);

    useImperativeHandle(ref, () => ({
        scrollToIndex: (index: number) => {
            if (!containerRef.current) return;

            containerRef.current.scrollTop = Math.max(0, index * itemHeight - height / 2 + itemHeight / 2);
        },
        getContainer: () => containerRef.current
    }));

    const handleScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            onScroll?.(e.currentTarget.scrollTop);

            if (rafRef.current !== null) return;
            rafRef.current = requestAnimationFrame(() => {
                const currentTop = containerRef.current?.scrollTop ?? 0;
                setScrollTop(currentTop);
                rafRef.current = null;
            });
        },
        [onScroll]
    );

    const totalHeight = items.length * itemHeight;

    const firstVisible = Math.floor(scrollTop / itemHeight);
    const startIndex = Math.max(0, firstVisible - overscan);
    const visibleCount = Math.ceil(height / itemHeight);
    const endIndex = Math.min(items.length, firstVisible + visibleCount + overscan);

    const visibleItems = items.slice(startIndex, endIndex);
    const offsetY = startIndex * itemHeight;

    return (
        <div
            ref={containerRef}
            className={className}
            onScroll={handleScroll}
            style={{
                height,
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative',
                ...style
            }}
        >
            <div style={{ height: totalHeight, position: 'relative' }}>
                <div
                    style={{
                        transform: `translateY(${offsetY}px)`,
                        willChange: 'transform'
                    }}
                >
                    {visibleItems.map((item, i) => (
                        <VirtualRow key={startIndex + i} height={itemHeight}>
                            {renderItem(item, startIndex + i)}
                        </VirtualRow>
                    ))}
                </div>
            </div>
        </div>
    );
}

const VirtualScrollList = forwardRef(VirtualScrollListInner) as <T>(
    props: VirtualScrollListProps<T> & { ref?: React.ForwardedRef<VirtualScrollListRef> }
) => React.ReactElement;

export default VirtualScrollList;
